import { useEffect, useRef, useState } from 'react';
import dynamic from 'next/dynamic';

import useSubmitPost from '@/hooks/scheduler/useSubmitPost';
// Utils
import useSchedulerPreviewPosts from '@/utils/hooks/scheduler/useSchedulerPreviewPosts';
import useTranslation from '@/utils/hooks/translation/useTranslation';
import useActiveGroup from '@/utils/hooks/user/useActiveGroup';
import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';
import useIsGroup from '@/utils/hooks/user/useIsGroup';
import { useGetFunnelCreationStatus } from '@/utils/scheduler/funnel/hooks/useGetFunnelCreationStatus';

import { classNames } from '@/utils/classNames';
import { put } from '@/utils/client';
import { injectEndpointUrlParams } from '@/utils/injectEndpointUrlParams';
import {
    getFeedMediaLibraryLocalStorage,
    setFeedMediaLibraryLocalStorage
} from '@/utils/localStorage/scheduler/feedMediaLibraryLocalStorage';
import useIntersectionObserver from '@/utils/hooks/utils/useIntersectionObserver';
import useToggle from '@/utils/hooks/utils/useToggle';

// Lib
import { EDIT_SCHEDULER_MASTER_POST, SCHEDULER_POST } from '@/lib/routes/apiEndpoints';
import { DRAFT } from '@/lib/scheduler/postStatus';

import { SizeMe } from '@/components/base';
// Components
import Button from '@/components/base/Button';
import Icon from '@/components/base/Icon';
import InfoBanner from '@/components/base/InfoBanner';
import StickySave from '@/components/composite/Form/StickySave';
import { DeletePostModal } from '@/components/pages/scheduler/deletePostModal/DeletePostModal';
import FeedHeaderFilter, {
    statusOptions
} from '@/components/pages/scheduler/feed/FeedHeaderFilter';
import FeedMobilePreview from '@/components/pages/scheduler/feed/FeedMobilePreview';
import { GlobalPreviewResponsive } from '@/components/pages/scheduler/feed/GlobalPreviewResponsive';
import SchedulerPostsList from '@/components/pages/scheduler/feed/SchedulerPostsList';

const SchedulerFunnel = dynamic(() =>
    import('@/components/composite/Scheduler/Funnel').then((mod) => mod.SchedulerFunnel)
);
const ModalDragAndDropPostCrosspost = dynamic(
    () => import('@/components/pages/scheduler/calendar/ModalDragAndDropPostCrosspost')
);

const FeedPreview = ({
    needReloadScheduledPosts = false,
    setNeedReloadScheduledPosts = () => {
        /** */
    }
}) => {
    const { t } = useTranslation('scheduler');
    const { activeSocialProfile } = useActiveSocialProfile();
    const { activeGroup } = useActiveGroup();
    const isGroup = useIsGroup();
    const { creation_type } = useGetFunnelCreationStatus({
        activeSocialProfile,
        activeGroup,
        isGroup
    });
    const {
        isOpen: isOpenPostFunnel,
        toggle: togglePostFunnel,
        params: paramsPostFunnel,
        setParams: setParamsPostFunnel
    } = useToggle();
    const [activeFilter, setActiveFilter] = useState(statusOptions[0]);
    const [postsSaving, setPostSaving] = useState({
        loading: false,
        error: false
    });
    const [deleteModal, setDeleteModal] = useState({
        post: null,
        isOpen: false
    });
    const [openMediaLibrary, setOpenMediaLibrary] = useState(getFeedMediaLibraryLocalStorage());
    const refBannerInfo = useRef();
    const { submitSinglePost } = useSubmitPost({ forcedSocialProfile: activeSocialProfile });
    const bannerInfoInViewPort = useIntersectionObserver(refBannerInfo, '0px');

    const {
        schedulerPreviewPosts,
        totalCount,
        schedulerPreviewPostsError,
        isLoadingInitialData,
        loadMoreSchedulerPreviewPosts,
        isLoadMore,
        isLoadingMore,
        mutateSchedulerPreviewPosts,
        setRequestParams,
        toSavePosts,
        setReorderedSchedulerPreviewPosts
    } = useSchedulerPreviewPosts({
        activeSPID: activeSocialProfile?.id,
        options: {
            revalidateAll: true
        }
    });

    useEffect(() => {
        if (needReloadScheduledPosts) {
            setNeedReloadScheduledPosts(false);
            mutateSchedulerPreviewPosts(null);
        }
    }, [mutateSchedulerPreviewPosts, needReloadScheduledPosts, setNeedReloadScheduledPosts]);

    const submitPost = async (post, updateCrossPosts) => {
        try {
            if (updateCrossPosts && post?.master_id) {
                return await put(
                    injectEndpointUrlParams(EDIT_SCHEDULER_MASTER_POST, {
                        MASTER_ID: post.master_id
                    }),
                    {
                        scheduled_at: post.scheduled_at,
                        scheduled_at_option: 'custom',
                        approval: post?.approval?.hash ? post.approval : []
                    }
                );
            }
            await put(
                injectEndpointUrlParams(SCHEDULER_POST, {
                    SPID: post.social_profile_id,
                    postId: post.id
                }),
                {
                    ...post,
                    scheduled_at_option: 'custom',
                    approval: post?.approval?.hash ? post.approval : []
                }
            );
        } catch (e) {
            setPostSaving((prevState) => ({
                ...prevState,
                loading: false,
                error: true
            }));
        }
    };

    const handleChanges = async (updateCrossPost = false) => {
        setPostSaving((prevState) => ({
            ...prevState,
            loading: true,
            error: false
        }));

        let postState = DRAFT;
        if (creation_type === 'create_reminder') {
            postState = 'reminder';
        }

        let hasError = false;

        // Sequential post creation to avoid backend transaction issues
        for (const toSavePost of toSavePosts) {
            try {
                if (toSavePost?.state) {
                    await submitPost(toSavePost, updateCrossPost);
                } else {
                    await submitSinglePost(
                        toSavePost.state
                            ? { ...toSavePost, scheduled_at_option: 'custom' }
                            : {
                                  ...toSavePost,
                                  id: null,
                                  state: postState,
                                  scheduled_at_option: 'custom',
                                  autopost: true
                              },
                        activeSocialProfile.id,
                        () => {
                            /** */
                        },
                        () => {
                            hasError = true;
                        },
                        false,
                        updateCrossPost
                    );
                }
            } catch {
                hasError = true;
                // Continue with next post
            }
        }

        await mutateSchedulerPreviewPosts();
        setReorderedSchedulerPreviewPosts(null);

        setPostSaving((prevState) => ({
            ...prevState,
            loading: false,
            error: hasError
        }));

        // Scroll To info banner if it's not visible in the viewport
        if (!bannerInfoInViewPort) {
            refBannerInfo?.current?.scrollIntoView({ behavior: 'smooth' });
        }
    };

    const saveChanges = () => {
        if (crossPostsPostsHasChanged) {
            toggleCrosspostDragAndDropModal();
        } else {
            handleChanges();
        }
    };

    // CrossPost drag and drop validate modal data
    const { isOpen: isOpenCrosspostDragAndDropModal, toggle: toggleCrosspostDragAndDropModal } =
        useToggle();
    const [crossPostsPostsHasChanged, setCrossPostsPostsHasChanged] = useState(false);
    // Add type
    const [reorderCrossPosts, setReorderCrossPosts] = useState([]);
    const checkCrossPostsPostsChanges = (reorderedPosts) => {
        let changes = false;
        // Start with a brand new array each time
        const newReorderCrossPosts = [];

        const crossPostsPosts = schedulerPreviewPosts?.filter(
            (profile) => profile?.cross_post_social_profiles?.length > 0 || profile?.master_id
        );

        if (crossPostsPosts.length > 0) {
            const crossPostsPostsIds = crossPostsPosts?.map((p) => p.id);
            const draggableReorderedPosts = reorderedPosts?.map((p) => p.id);
            const draggablePosts = schedulerPreviewPosts?.map((p) => p.id);

            // check if crossposts posts position have changed in array
            for (let i = 0; i < crossPostsPostsIds.length; i++) {
                const el = crossPostsPostsIds[i];
                if (draggableReorderedPosts?.indexOf(el) !== draggablePosts?.indexOf(el)) {
                    // Add to the new array (no need to check if already exists since it's a new array)
                    newReorderCrossPosts.push(crossPostsPosts.find((p) => p.id === el));
                    changes = true;
                }
            }
        }

        // Set the brand new array
        setReorderCrossPosts(newReorderCrossPosts);
        return changes;
    };

    const getErrorMessage = () => {
        if (postsSaving.error) {
            if (typeof postsSaving.error === 'string') {
                return postsSaving.error;
            }
            return t('errors:error_occured_save_data');
        }

        return t('scheduler:feed.error_fetch_feed');
    };

    return (
        <div className="flex h-full w-full flex-col">
            <div ref={refBannerInfo} />
            {schedulerPreviewPostsError || postsSaving.error ? (
                <InfoBanner
                    title={t('commons:error')}
                    message={getErrorMessage()}
                    className="mb-8 w-full"
                    onClose={
                        postsSaving.error
                            ? () =>
                                  setPostSaving((prevState) => ({
                                      ...prevState,
                                      error: false
                                  }))
                            : null
                    }
                />
            ) : null}
            <div className="flex h-full w-full">
                <div className="block h-full w-full lg:hidden">
                    <div className="flex h-full w-full flex-col items-center">
                        <GlobalPreviewResponsive>
                            <div className="flex w-full justify-end">
                                <div className="relative flex h-full w-full max-w-[390px] flex-col space-y-4 overflow-y-scroll">
                                    <FeedMobilePreview
                                        posts={schedulerPreviewPosts}
                                        activeFilter={activeFilter}
                                        resetFilters={() => {
                                            setActiveFilter(statusOptions[0]);
                                            setRequestParams({});
                                        }}
                                        isLoading={isLoadingInitialData}
                                        isSaving={postsSaving.loading}
                                        isLoadingMore={isLoadingMore}
                                        isLoadMore={isLoadMore}
                                        loadMore={loadMoreSchedulerPreviewPosts}
                                        reorderPosts={(reorderedPosts) => {
                                            setReorderedSchedulerPreviewPosts(reorderedPosts);
                                        }}
                                        setDeleteModal={setDeleteModal}
                                    />
                                </div>
                            </div>
                            <div className="flex h-full flex-1 flex-col">
                                <div className="flex flex-col space-y-2 pb-8">
                                    <FeedHeaderFilter
                                        activeFilter={activeFilter}
                                        setActiveFilter={setActiveFilter}
                                        isLoading={isLoadingInitialData}
                                        itemsNumber={totalCount}
                                        changeStatusFilter={(filter) => {
                                            setRequestParams(
                                                filter.value === 'all'
                                                    ? {}
                                                    : { filter_state: filter.value }
                                            );
                                        }}
                                    />
                                    <p className="text-sm text-icoGray-500">
                                        {t('feed.introduction')}
                                    </p>
                                </div>
                                <div className="flex h-full flex-1 overflow-hidden pb-m">
                                    <SchedulerPostsList
                                        schedulerPosts={schedulerPreviewPosts}
                                        isLoading={isLoadingInitialData}
                                        isLoadingMore={isLoadingMore}
                                        isLoadMore={isLoadMore}
                                        loadMore={loadMoreSchedulerPreviewPosts}
                                        setDeleteModal={setDeleteModal}
                                        reorderPosts={(reorderedPosts) => {
                                            setReorderedSchedulerPreviewPosts(reorderedPosts);
                                        }}
                                        activeFilter={activeFilter}
                                        onClickEditPost={(post) => {
                                            setParamsPostFunnel({
                                                type: 'edit',
                                                postId: post.id,
                                                socialProfileId: post.social_profile_id
                                            });
                                            togglePostFunnel();
                                        }}
                                        openMediaLibrary={openMediaLibrary}
                                    />
                                </div>
                            </div>
                        </GlobalPreviewResponsive>
                    </div>
                </div>
                <div className="mr-12 hidden h-full w-full lg:block">
                    <div className="flex h-full w-full gap-x-4">
                        <div
                            className={classNames('flex h-full flex-col pl-1 pt-1', {
                                'w-1/2': !openMediaLibrary,
                                'w-1/3': openMediaLibrary
                            })}>
                            <div className="flex flex-col space-y-2 pb-8">
                                <FeedHeaderFilter
                                    activeFilter={activeFilter}
                                    setActiveFilter={setActiveFilter}
                                    isLoading={isLoadingInitialData}
                                    itemsNumber={totalCount}
                                    changeStatusFilter={(filter) => {
                                        setRequestParams(
                                            filter?.value === 'all'
                                                ? {}
                                                : { filter_state: filter?.value }
                                        );
                                    }}
                                />
                                <p className="text-sm text-icoGray-500">{t('feed.introduction')}</p>
                            </div>
                            <div className="flex h-full flex-1 overflow-hidden pb-m">
                                <SchedulerPostsList
                                    schedulerPosts={schedulerPreviewPosts}
                                    isLoading={isLoadingInitialData}
                                    isLoadingMore={isLoadingMore}
                                    isLoadMore={isLoadMore}
                                    loadMore={loadMoreSchedulerPreviewPosts}
                                    setDeleteModal={setDeleteModal}
                                    onClickEditPost={(post) => {
                                        setParamsPostFunnel({
                                            type: 'edit',
                                            postId: post.id,
                                            socialProfileId: post.social_profile_id
                                        });
                                        togglePostFunnel();
                                    }}
                                    activeFilter={activeFilter}
                                    reorderPosts={(reorderedPosts) => {
                                        setReorderedSchedulerPreviewPosts(reorderedPosts);
                                    }}
                                    openMediaLibrary={openMediaLibrary}
                                />
                            </div>
                        </div>
                        <button
                            onClick={() => {
                                setFeedMediaLibraryLocalStorage(!openMediaLibrary);
                                setOpenMediaLibrary(!openMediaLibrary);
                            }}
                            className={classNames(
                                'absolute right-1 z-10 h-12 w-12 rounded-md bg-white dark:bg-icoDarkMode-deepBlack',
                                {
                                    'text-icoBlue': openMediaLibrary,
                                    'text-icoGray-500 dark:text-icoDarkMode-wolf':
                                        !openMediaLibrary,
                                    'top-1': !postsSaving.error,
                                    'top-24': postsSaving.error || schedulerPreviewPostsError
                                }
                            )}>
                            <Icon icon="media-library" />
                        </button>
                        <div className="flex-1">
                            <SizeMe monitorHeight>
                                {({ size }) => (
                                    <div
                                        className={classNames(
                                            'relative mx-auto mt-4 flex h-full flex-col',
                                            {
                                                'w-[384px] items-end': !openMediaLibrary,
                                                'w-[600px] items-center xl:w-[770px]':
                                                    openMediaLibrary
                                            }
                                        )}>
                                        <div className="absolute left-0 top-0 h-full w-full">
                                            <FeedMobilePreview
                                                posts={schedulerPreviewPosts}
                                                activeFilter={activeFilter}
                                                resetFilters={() => {
                                                    setActiveFilter(statusOptions[0]);
                                                    setRequestParams({});
                                                }}
                                                contentHeight={size.height}
                                                isLoading={isLoadingInitialData}
                                                isSaving={postsSaving.loading}
                                                isLoadingMore={isLoadingMore}
                                                isLoadMore={isLoadMore}
                                                loadMore={loadMoreSchedulerPreviewPosts}
                                                reorderPosts={(reorderedPosts) => {
                                                    setReorderedSchedulerPreviewPosts(
                                                        reorderedPosts
                                                    );
                                                    const changes =
                                                        checkCrossPostsPostsChanges(reorderedPosts);
                                                    setCrossPostsPostsHasChanged(changes);
                                                }}
                                                setDeleteModal={setDeleteModal}
                                                openMediaLibrary={openMediaLibrary}
                                                onCloseMediaLibrary={() =>
                                                    setOpenMediaLibrary(false)
                                                }
                                            />
                                        </div>
                                    </div>
                                )}
                            </SizeMe>
                        </div>
                    </div>
                </div>
            </div>
            <div className="fixed left-0 z-20 w-full text-center">
                <StickySave variant="lg" className="!flex" isVisible={toSavePosts?.length > 0}>
                    <Button
                        size="sm"
                        className="mr-6"
                        variant="secondary"
                        disabled={postsSaving.loading}
                        onClick={() => {
                            if (!postsSaving.loading) {
                                setReorderedSchedulerPreviewPosts(null);
                            }
                        }}>
                        {t('feed.cancel_changes')}
                    </Button>
                    <Button
                        size="sm"
                        isLoading={postsSaving.loading}
                        disabled={postsSaving.loading}
                        onClick={() => {
                            if (!postsSaving.loading) {
                                saveChanges();
                            }
                        }}>
                        {t('feed.save_changes')}
                    </Button>
                </StickySave>
            </div>
            <SchedulerFunnel
                isOpen={isOpenPostFunnel}
                toggle={togglePostFunnel}
                settings={paramsPostFunnel}
                callbackSuccess={() => mutateSchedulerPreviewPosts(null)}
            />
            <DeletePostModal
                isOpen={deleteModal.isOpen}
                toggle={() =>
                    setDeleteModal({
                        post: null,
                        isOpen: false
                    })
                }
                post={deleteModal.post}
                deleteSuccess={() => {
                    mutateSchedulerPreviewPosts(
                        (prevState) =>
                            prevState.map((page) => ({
                                ...page,
                                posts: page.posts.filter((post) => post.id !== deleteModal.post.id),
                                summary: {
                                    ...page.summary,
                                    total_count: page.summary.total_count - 1
                                }
                            })),
                        false
                    );
                }}
            />
            <ModalDragAndDropPostCrosspost
                isOpen={isOpenCrosspostDragAndDropModal}
                posts={reorderCrossPosts}
                onClickThisProfile={() => {
                    handleChanges();
                    toggleCrosspostDragAndDropModal();
                }}
                onClickAllProfiles={() => {
                    handleChanges(true);
                    toggleCrosspostDragAndDropModal();
                }}
                cancel={() => {
                    setReorderedSchedulerPreviewPosts(null);
                    toggleCrosspostDragAndDropModal();
                }}
                multiple={true}
            />
        </div>
    );
};

export default FeedPreview;
